import { useCallback, useEffect, useRef } from 'react'

type PositionedMouseListenerOptions = {
  maxDiff?: number
  stopPropagation?: boolean
  /** How long to wait to confirm it wasn't a double/triple click (ms) */
  releaseDelay?: number
  /** Ignore clicks that land within an existing text selection */
  ignoreClicksOnSelection?: boolean
}

function isPointInRect(x: number, y: number, r: DOMRect, pad = 1) {
  return x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad
}

function isClickOnSelection(e: React.MouseEvent) {
  const target = e.target as Node | null

  // Inputs/Textareas: use their own selection range
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    const el = target
    if (el.selectionStart != null && el.selectionEnd != null) {
      return el.selectionStart !== el.selectionEnd
    }
  }

  const sel = window.getSelection?.()
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false

  try {
    const range = sel.getRangeAt(0)

    // Fast path: is the clicked node within the selection?
    // @ts-ignore - containsNode exists in most browsers
    if (typeof (sel as any).containsNode === 'function' && target) {
      // @ts-ignore
      if ((sel as any).containsNode(target, true)) return true
    }

    // Precise hit-test: is the point inside any selection rect?
    const rects = range.getClientRects()
    for (let i = 0; i < rects.length; i++) {
      if (isPointInRect(e.clientX, e.clientY, rects[i], 1)) return true
    }
  } catch {
    // ignore edge-case errors
  }

  return false
}

export const usePositionedMouseListeners = (
  onClick: (e: React.MouseEvent) => void,
  {
    maxDiff = 2,
    stopPropagation,
    releaseDelay = 10,
    ignoreClicksOnSelection = true,
  }: PositionedMouseListenerOptions = {
    maxDiff: 2,
  },
) => {
  const mouseDownRef = useRef<{ x: number; y: number }>()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastEligibleEventRef = useRef<React.MouseEvent | null>(null)

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => () => clearTimer(), [])

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (stopPropagation !== false) {
        e.stopPropagation()
      }
      mouseDownRef.current = { x: e.clientX, y: e.clientY }
      clearTimer()
    },
    [stopPropagation],
  )

  const onMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (stopPropagation !== false) e.stopPropagation()

      // Always cancel any pending single-click first.
      clearTimer()

      // Ignore multi-clicks (double, triple, etc.)
      if (e.detail > 1) {
        lastEligibleEventRef.current = null
        return
      }

      // Ignore if clicking inside an existing selection
      if (ignoreClicksOnSelection && isClickOnSelection(e)) {
        lastEligibleEventRef.current = null
        return
      }

      // Validate small movement from mousedown to mouseup
      if (!mouseDownRef.current) return
      const diffX = Math.abs(e.clientX - mouseDownRef.current.x)
      const diffY = Math.abs(e.clientY - mouseDownRef.current.y)
      if (diffX > maxDiff || diffY > maxDiff)
        return // Queue potential single-click; if another click happens before
        // the delay elapses, the next onMouseUp will clear this timer.
      ;(e as any).persist?.()
      lastEligibleEventRef.current = e
      timerRef.current = setTimeout(() => {
        if (lastEligibleEventRef.current) {
          onClick(lastEligibleEventRef.current)
        }
        lastEligibleEventRef.current = null
        timerRef.current = null
      }, releaseDelay)
    },
    [ignoreClicksOnSelection, maxDiff, onClick, stopPropagation, releaseDelay],
  )

  return { onMouseDown, onMouseUp }
}
